import { throttle } from 'lodash';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, Center, HStack, rem, SlideFade, SparklesOutline, Spinner, Stack, StopSolid, Text } from '@bonusly/design-system';

import { AudioState, SourceMode } from './hooks/use-audio-transcription';

type TranscriptionVisualizerProps = {
	onAccept: () => void;
	onReject: () => void;
	onStop: () => void;
	mode: SourceMode;
	state: AudioState;
};

export type TranscriptionVisualizerRef = {
	draw: (data: ArrayBuffer) => void;
};

// The number of bars to show in the waveform
const VISUALIZE_CHUNK_COUNT = 40;
// The value of the data in the array ranges generally from 0 to ~5000. If we exceed that value we'll
// scale the data down to fit the canvas.
const MAX_BAR_HEIGHT = 2000;
// Similarly, if the user isn't speaking there might be background noise that causes the bars to "jiggle".
// Setting a minimum will flatten out the waveform when it's quiet.
const MIN_BAR_HEIGHT = 50;

type StateRef = {
	count: number;
	timer: any;
	stage: number;
	visible: boolean;
};

// Every 5 seconds will show text for 3 seconds
const useVisualizerText = (state: AudioState) => {
	const { t } = useTranslation('feedback', {
		keyPrefix: 'transcribe',
	});

	// Everything is stored in a ref because it's accessed in an interval, which is a closure,
	// and we don't want to have to recreate the interval every time the state changes.
	const stateRef = useRef<StateRef>({
		count: 0,
		timer: null,
		stage: 0,
		visible: false,
	});

	const randomStageBlurbs = useMemo(() => {
		return [
			Array.from({ length: 5 }, (_, i) => t(`fb_starting_${i + 1}`)),
			Array.from({ length: 10 }, (_, i) => t(`fb_recording_${i + 1}`)),
			Array.from({ length: 5 }, (_, i) => t(`fb_stopping_${i + 1}`)),
		];
	}, [t]);

	// The are the only values that should trigger a re-render so they're stored in state
	const [text, setText] = useState('');
	const [isVisible, setIsVisible] = useState(false);

	// We'll stop the timer whenever the component is unmounted
	useEffect(() => {
		return () => {
			if (stateRef.current.timer) {
				// The timer is only set once externally and removed when this component is unmounted so
				// there's no way to save the reference in this effect. There are also other checks to
				// make sure the timer is cleared when it's no longer needed. (ex. line 121)
				// eslint-disable-next-line react-hooks/exhaustive-deps
				clearTimeout(stateRef.current.timer);
			}
		};
	}, []);

	// I don't like this and am going to figure out how to remove it
	useEffect(() => {
		if (state === AudioState.Stopping) {
			stateRef.current.stage = 2;
		}

		if (state === AudioState.Complete) {
			stateRef.current.stage = 3;
		}
	}, [state]);

	// When we start the feedback text display we just increment a timer every second.
	// We'll show one message in phase 0, which tells the user to start speaking.
	// Then we'll stay in phase 1 while the user is recording, which will show a random message every 10 seconds.
	// When the user clicks stop we'll show random messages from phase 2, which tells them that we're reviewing
	// the audio and providing updates.
	const start = () => {
		const getText = () => {
			const stage = stateRef.current.stage;
			const translations = randomStageBlurbs[stage];
			return translations[Math.floor(Math.random() * translations.length)];
		};

		if (stateRef.current.timer) {
			clearTimeout(stateRef.current.timer);
		}

		stateRef.current.timer = setInterval(() => {
			const count = stateRef.current.count++;
			const phase = count % 10;

			if (phase === 2) {
				setIsVisible(true);
				setText(getText());

				if (stateRef.current.stage === 0) {
					stateRef.current.stage = 1;
				}

				if (stateRef.current.stage === 3) {
					clearTimeout(stateRef.current.timer);
				}
			}

			if (phase === 7) {
				setIsVisible(false);
			}
		}, 1000);
	};

	const stop = () => {
		stateRef.current.stage++;
	};

	return {
		stop,
		start,
		isVisible,
		text,
	};
};

export const TranscriptionVisualizer = forwardRef<TranscriptionVisualizerRef, TranscriptionVisualizerProps>(
	({ mode, onAccept, onReject, onStop, state }, ref) => {
		const feedback = useVisualizerText(state);
		const canvasRef = useRef<HTMLCanvasElement>(null);
		const { t } = useTranslation('feedback', {
			keyPrefix: 'transcribe',
		});

		// Essentially what this does is gets the ArrayBuffer of raw audio data. We turn it into an Int16Array,
		// which is what it is sent to us as (but converted to a buffer for transmission). We chunk it into the number
		// of bars we want to show in the waveform and figure out the average value of each chunk. Then we do some normalizing
		// because otherwise the bars would be all over the place. We then draw the bars on the canvas.
		const draw = useCallback((buffer: ArrayBuffer) => {
			const view = new Int16Array(buffer);
			const size = Math.floor(view.length / VISUALIZE_CHUNK_COUNT);
			const data = [];

			for (let i = 0; i < VISUALIZE_CHUNK_COUNT; i++) {
				const start = i * size;
				let sum = 0;

				for (let j = 0; j < size; j++) {
					sum += Math.abs(view[start + j]);
				}

				data.push(sum / size);
			}

			const l = data.length;
			const max = Math.max(...data);

			for (let i = 0; i < l; i++) {
				if (data[i] < MIN_BAR_HEIGHT) {
					data[i] = MIN_BAR_HEIGHT;
				}
			}

			if (max > MAX_BAR_HEIGHT) {
				for (let i = 0; i < l; i++) {
					data[i] = (data[i] / max) * MAX_BAR_HEIGHT;
				}
			}

			for (let i = 0; i < l; i++) {
				data[i] = data[i] / MAX_BAR_HEIGHT;
			}

			const canvas = canvasRef.current;

			if (!canvas) {
				return;
			}

			const ctx = canvas.getContext('2d');

			if (!ctx) {
				return;
			}

			const width = canvas.width;
			const height = canvas.height;

			ctx.clearRect(0, 0, width, height);

			const barWidth = width / l;
			const gap = 2;
			const adjustedBarWidth = barWidth - gap;

			for (let i = 0; i < l; i++) {
				const multiplier = data[i];
				const barHeight = height * multiplier;
				const x = i * barWidth + gap / 2;
				const y = height / 2 - barHeight / 2;

				const diff = Math.abs(i - l / 2);
				const opacity = 1 - (diff / (l / 2)) * 0.8;
				ctx.fillStyle = `rgba(74, 144, 226, ${opacity})`;
				ctx.fillRect(x, y, adjustedBarWidth, barHeight);
			}
		}, []);

		// The data from the mic comes in super fast so this slows it down a bit
		const throttledDraw = useRef(throttle(draw, 50));

		useImperativeHandle(ref, () => ({
			draw: (data) => throttledDraw.current?.(data),
		}));

		const showStop = state === AudioState.Recording;
		const showActive = state === AudioState.Stopping || state === AudioState.Rephrasing;

		const handleClickAccept = () => {
			feedback.start();
			onAccept();
		};

		const handleClickStop = () => {
			feedback.stop();
			onStop();
		};

		return (
			<Stack minH={rem(190)} borderRadius="md" border="1px solid" borderColor="inherit" p={2}>
				<Center pos="relative" flexGrow={1} mb={2} mt={4}>
					<canvas width="300px" height="100px" ref={canvasRef} />

					{showStop && (
						<Box
							as="button"
							alignItems="center"
							bg="primary.bg"
							border="2px solid white"
							borderRadius="full"
							boxSize={rem(36)}
							color="white"
							display="flex"
							justifyContent="center"
							onClick={handleClickStop}
							pos="absolute"
							right="calc(50% - 18px)"
							top="calc(50% - 18px)"
							transitionProperty="common"
							transitionDuration="normal"
							type="button"
							_hover={{
								bg: 'primary.bgHover',
							}}
						>
							<StopSolid size="xl" />
						</Box>
					)}

					{showActive && (
						<Box
							alignItems="center"
							bg="bg.surface"
							border="2px solid white"
							borderRadius="full"
							boxSize={rem(36)}
							color="primary.bg"
							display="flex"
							justifyContent="center"
							pos="absolute"
							right="calc(50% - 18px)"
							top="calc(50% - 18px)"
						>
							<Spinner color="primary.bg" boxSize={rem(24)} />
						</Box>
					)}
				</Center>

				{mode === SourceMode.Unselected && (
					<Box>
						<Center mb={2}>
							<Text color="text.softest" size="sm">
								{t('improve_audio')}
							</Text>
						</Center>

						<HStack justifyContent="center" columnGap={2}>
							<Button variant="alpha" size="sm" onClick={onReject}>
								{t('improve_reject')}
							</Button>
							<Button onClick={handleClickAccept} size="sm">
								<SparklesOutline />
								{t('improve_accept')}
							</Button>
						</HStack>
					</Box>
				)}

				{mode === SourceMode.Rephrased && (
					<Center mb={2}>
						<SlideFade in={feedback.isVisible} offsetY="10px">
							<Text color="text.softest" size="sm">
								{feedback.text || '-'}
							</Text>
						</SlideFade>
					</Center>
				)}
			</Stack>
		);
	}
);

TranscriptionVisualizer.displayName = 'TranscriptionVisualizer';
